import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tabs,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { IconBriefcase, IconCheck, IconExternalLink, IconMail, IconPhone, IconX } from "@tabler/icons-react";
import useSWR from "swr";
import { fetcher } from "../../../services/fetcher";
import {
  errorNotifications,
  successNotifications,
} from "../../../utils/handleNotifications";
import {
  PAYMENT_GATEWAY_OPTIONS,
  QUOTATION_STATUS_OPTIONS,
} from "../utils/leadsOptions";
import LeadFollowups from "./LeadFollowups";
import classes from "./QuotationDrawer.module.css";

const INITIAL_FOLLOWUP = {
  person_name: "",
  phone: "",
  due_date: null,
  followup_type: "OUTBOUND_CALL",
  priority: "MEDIUM",
  trek: "",
  notes: "",
};

const fmtDate = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : null);

const PaymentLinkBox = ({ url }) => {
  if (!url) return null;
  return (
    <Box className={classes.paymentLinkBox}>
      <Anchor
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.paymentLinkAnchor}
        underline="never"
      >
        Open Payment Link
        <IconExternalLink size={14} stroke={2} />
      </Anchor>
      <Text className={classes.paymentLinkUrl}>{url}</Text>
    </Box>
  );
};

const StatusPill = ({ label, tone = "neutral" }) => (
  <span className={`${classes.pill} ${classes[`pill${tone}`]}`}>
    <span className={classes.pillDot} />
    {label}
  </span>
);

const OverviewSection = ({ title, children }) => (
  <Stack gap={2}>
    <Text className={classes.sectionTitle}>{title}</Text>
    {children}
    <Divider className={classes.sectionDivider} />
  </Stack>
);

const OverviewRow = ({ label, value }) => (
  <div className={classes.fieldRow}>
    <Text className={classes.fieldLabel}>{label}</Text>
    <Text className={classes.fieldValue}>{value ?? "—"}</Text>
  </div>
);

const tripDays = (quote) => {
  if (quote?.days != null && quote.days !== "") return quote.days;
  if (!quote?.start_date || !quote?.end_date) return null;
  return dayjs(quote.end_date).diff(dayjs(quote.start_date), "day") + 1;
};

const formatAges = (quote) => {
  if (Array.isArray(quote?.traveller_ages)) return quote.traveller_ages.join(", ") || "0";
  if (quote?.traveller_ages != null && quote?.traveller_ages !== "") return String(quote.traveller_ages);
  return "0";
};

const leadLabel = (quote) =>
  quote?.lead_no || (quote?.lead ? `LID-${String(quote.lead).padStart(4, "0")}` : null);

const mapForm = (q) => ({
  customer_name: q?.customer_name || "",
  phone: q?.phone || "",
  email: q?.email || "",
  trek: q?.trek || "",
  persons: q?.persons ?? 1,
  start_date: q?.start_date ? dayjs(q.start_date).toDate() : null,
  end_date: q?.end_date ? dayjs(q.end_date).toDate() : null,
  nationality: q?.nationality || "",
  travelling_from: q?.travelling_from || "",
  travelling_to: q?.travelling_to || "",
  traveller_ages: Array.isArray(q?.traveller_ages)
    ? q.traveller_ages.join(", ")
    : q?.traveller_ages || "",
  plan_name: q?.plan_name || "",
  premium: q?.premium || "",
  discount: q?.discount || "",
  notes: q?.notes || "",
});

const QuotationDrawer = ({
  opened,
  onClose,
  quotationId,
  initialTab = "overview",
  onUpdated,
}) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [tab, setTab] = useState("overview");
  const [form, setForm] = useState(null);
  const [gateway, setGateway] = useState("");
  const [linkData, setLinkData] = useState(null);
  const [makePaymentLink, setMakePaymentLink] = useState(true);
  const [followupForm, setFollowupForm] = useState(INITIAL_FOLLOWUP);
  const [busy, setBusy] = useState("");

  const detailUrl = opened && quotationId ? `crm/quotations/${quotationId}/` : null;
  const { data: quote, isLoading, mutate } = useSWR(detailUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const followupsUrl =
    opened && quotationId ? `crm/followups/?quotation=${quotationId}&ordering=due_date` : null;
  const {
    data: followupsData,
    isLoading: isFollowupsLoading,
    mutate: mutateFollowups,
  } = useSWR(followupsUrl, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const followups = useMemo(() => {
    const rows = followupsData?.results || followupsData?.data || followupsData || [];
    return Array.isArray(rows) ? rows : [];
  }, [followupsData]);

  useEffect(() => {
    if (!opened) return;
    setTab(initialTab || "overview");
  }, [opened, initialTab]);

  useEffect(() => {
    if (!quote) return;
    setForm(mapForm(quote));
    setGateway(quote.payment_gateway || "");
    setFollowupForm((prev) => ({
      ...prev,
      person_name: quote.customer_name || "",
      phone: quote.phone || "",
      trek: quote.trek || "",
    }));
    setMakePaymentLink(quote.make_payment_or_not ?? true);
    const link = quote.quote_link || quote.unique_link;
    if (link) {
      setLinkData({ unique_link: link, expires_on: quote.expires_on });
    } else {
      setLinkData(null);
    }
  }, [quote]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const patchQuote = async (payload, msg) => {
    setBusy("save");
    try {
      await fetcher(`crm/quotations/${quotationId}/`, "PATCH", payload);
      successNotifications(msg || "Quotation updated");
      await mutate();
      if (onUpdated) await onUpdated();
    } catch (err) {
      errorNotifications(err?.response?.data || "Update failed", err);
    } finally {
      setBusy("");
    }
  };

  const handleStatusChange = (value) => {
    if (!value) return;
    patchQuote({ status: value }, "Status updated");
  };

  const handleSave = () => {
    if (!form) return;
    const ages = (form.traveller_ages || "")
      .split(",")
      .map((v) => Number(v.trim()))
      .filter((v) => !Number.isNaN(v));
    patchQuote({
      customer_name: form.customer_name,
      phone: form.phone,
      email: form.email,
      trek: form.trek,
      persons: Number(form.persons || 0),
      nationality: form.nationality,
      travelling_from: form.travelling_from,
      travelling_to: form.travelling_to,
      traveller_ages: ages,
      plan_name: form.plan_name,
      premium: form.premium || null,
      discount: form.discount || null,
      notes: form.notes,
    });
  };

  const handleSendEmail = async () => {
    if (!quotationId) return;
    setBusy("email");
    try {
      const linkRes = await fetcher(`crm/quotations/${quotationId}/generate_link/`, "POST", {
        notify: false,
      });
      const link = linkRes?.quote_link || linkRes?.unique_link;
      if (link) setLinkData({ unique_link: link, expires_on: linkRes?.expires_on });

      const res = await fetcher(`crm/quotations/${quotationId}/send_email/`, "POST", {});
      successNotifications(res?.detail || "Email sent");
      await mutate();
      if (onUpdated) await onUpdated();
    } catch (err) {
      errorNotifications(err?.response?.data || "Failed to send email", err);
    } finally {
      setBusy("");
    }
  };

  const handleSaveGateway = () => patchQuote({ payment_gateway: gateway || null }, "Gateway saved");

  const handleFollowupFormChange = (updates) => {
    setFollowupForm((prev) => ({ ...prev, ...updates }));
  };

  const handleFollowupSubmit = async (event) => {
    event.preventDefault();
    if (!quotationId) return;
    setBusy("followup");
    try {
      await fetcher(`crm/quotations/${quotationId}/followups/`, "POST", {
        person_name: followupForm.person_name,
        phone: followupForm.phone,
        due_date: fmtDate(followupForm.due_date),
        followup_type: followupForm.followup_type,
        priority: followupForm.priority,
        trek: followupForm.trek,
        notes: followupForm.notes,
      });
      setFollowupForm((prev) => ({ ...prev, due_date: null, notes: "" }));
      successNotifications("Follow-up scheduled");
      await mutateFollowups();
    } catch (err) {
      errorNotifications(err?.response?.data || "Failed to schedule follow-up", err);
    } finally {
      setBusy("");
    }
  };

  const handleGenerateLink = async () => {
    setBusy("link");
    try {
      const res = await fetcher(`crm/quotations/${quotationId}/generate_link/`, "POST", {});
      const link = res?.quote_link || res?.unique_link;
      if (link) setLinkData({ unique_link: link, expires_on: res?.expires_on });
      successNotifications(res?.status || res?.detail || "Payment link generated");
      await mutate();
      if (onUpdated) await onUpdated();
    } catch (err) {
      errorNotifications(err?.response?.data || "Failed to generate link", err);
    } finally {
      setBusy("");
    }
  };

  const handleMakePaymentToggle = async (checked) => {
    setMakePaymentLink(checked);
    await patchQuote({ make_payment_or_not: checked }, "Payment link preference updated");
  };

  const requirements = [
    { label: "Cover Pricing selected", ok: !!(quote?.cover_pricing ?? quote?.cover) },
    { label: "Start Date set", ok: !!quote?.start_date },
    { label: "End Date set", ok: !!quote?.end_date },
    { label: "Customer Email set", ok: !!quote?.email },
  ];
  const canGenerate = requirements.every((r) => r.ok) && makePaymentLink;

  const paymentUrl =
    linkData?.unique_link || quote?.quote_link || quote?.unique_link || null;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : "45%"}
      title={
        <Stack gap={0} className={classes.header}>
          <Text className={classes.quotNo}>{quote?.quot_no || "—"}</Text>
          <Text className={classes.customerName}>{quote?.customer_name || "Quotation"}</Text>
          <Group gap={6} className={classes.badgeRow}>
            {quote?.quotation_type ? (
              <StatusPill label={quote.quotation_type} tone="green" />
            ) : null}
            {quote?.status ? <StatusPill label={quote.status} tone="blue" /> : null}
            {leadLabel(quote) ? (
              <StatusPill label={`Lead: ${leadLabel(quote)}`} tone="neutral" />
            ) : null}
          </Group>
        </Stack>
      }
    >
      {isLoading && !quote ? (
        <Text size="sm" c="dimmed">
          Loading...
        </Text>
      ) : (
        <Stack gap="sm">
          <div className={classes.statusRow}>
            <Text className={classes.statusLabel}>Status:</Text>
            <Select
              data={QUOTATION_STATUS_OPTIONS}
              value={quote?.status || "DRAFT"}
              onChange={handleStatusChange}
              size="xs"
              maw={160}
            />
          </div>

          <div className={classes.contactRow}>
            <span className={classes.contactItem}>
              <IconPhone size={14} stroke={1.6} />
              {quote?.phone || "—"}
            </span>
            <span className={classes.contactSep}>·</span>
            <span className={classes.contactItem}>
              <IconMail size={14} stroke={1.6} />
              {quote?.email || "—"}
            </span>
            <span className={classes.contactSep}>·</span>
            <span className={classes.contactItem}>
              <IconBriefcase size={14} stroke={1.6} />
              {quote?.trek || "—"}
            </span>
          </div>

          <Tabs
            value={tab}
            onChange={setTab}
            variant="default"
            classNames={{ list: classes.tabsList, tab: classes.tab }}
          >
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="edit">Edit</Tabs.Tab>
              <Tabs.Tab value="followups">Follow-ups</Tabs.Tab>
              <Tabs.Tab value="payment-link">Payment Link</Tabs.Tab>
            </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="xs" className={classes.overviewScroll}>
              <OverviewRow label="Email" value={quote?.email} />
              <OverviewRow label="Persons" value={quote?.persons} />
              <OverviewSection title="Activity & Journey">
                <OverviewRow label="Trek" value={quote?.trek} />
                <OverviewRow label="Start Date" value={fmtDate(quote?.start_date)} />
                <OverviewRow label="End Date" value={fmtDate(quote?.end_date)} />
                <OverviewRow label="Days" value={tripDays(quote)} />
                <OverviewRow label="Nationality" value={quote?.nationality} />
                <OverviewRow label="From" value={quote?.travelling_from} />
                <OverviewRow label="To" value={quote?.travelling_to} />
                <OverviewRow label="Ages" value={formatAges(quote)} />
              </OverviewSection>
              <OverviewSection title="Plan & Premium">
                <OverviewRow label="Plan Name" value={quote?.plan_name || quote?.cover_name} />
                <OverviewRow label="Notes" value={quote?.notes} />
              </OverviewSection>
              <OverviewSection title="Payment Link">
                {paymentUrl ? <PaymentLinkBox url={paymentUrl} /> : <OverviewRow label="Link" value={null} />}
              </OverviewSection>
              <Stack gap="xs">
                <Text className={classes.sectionTitle}>Actions</Text>
                <Button
                  className={classes.sendEmailBtn}
                  leftSection={<IconMail size={16} />}
                  onClick={handleSendEmail}
                  loading={busy === "email"}
                  w={isMobile ? "100%" : "fit-content"}
                >
                  Send Email
                </Button>
                <Text className={classes.actionHint}>
                  Sends B2B or B2C proposal email to the customer.
                </Text>
              </Stack>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="edit" pt="md">
          {form && (
            <Stack gap="md">
              <Text size="xs" c="dimmed" fw={600}>CUSTOMER INFO</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Customer Name" required value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} />
                <TextInput label="Phone" required value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
                <TextInput label="Email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
              </SimpleGrid>
              <Text size="xs" c="dimmed" fw={600}>ACTIVITY & JOURNEY</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Activity" value={form.trek} onChange={(e) => setField("trek", e.target.value)} />
                <TextInput label="Persons" value={String(form.persons)} onChange={(e) => setField("persons", e.target.value)} />
                <DatePickerInput label="Start Date" value={form.start_date} onChange={(v) => setField("start_date", v)} />
                <DatePickerInput label="End Date" value={form.end_date} onChange={(v) => setField("end_date", v)} />
                <TextInput label="Nationality" value={form.nationality} onChange={(e) => setField("nationality", e.target.value)} />
                <TextInput label="Travelling From" value={form.travelling_from} onChange={(e) => setField("travelling_from", e.target.value)} />
                <TextInput label="Travelling To" value={form.travelling_to} onChange={(e) => setField("travelling_to", e.target.value)} />
                <TextInput label="Traveller Ages" value={form.traveller_ages} onChange={(e) => setField("traveller_ages", e.target.value)} />
              </SimpleGrid>
              <Text size="xs" c="dimmed" fw={600}>PREMIUM & COMMERCIAL</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Plan Name" value={form.plan_name} onChange={(e) => setField("plan_name", e.target.value)} />
                <TextInput label="Premium (Rs)" value={String(form.premium)} onChange={(e) => setField("premium", e.target.value)} />
                <TextInput label="Discount (Rs)" value={String(form.discount)} onChange={(e) => setField("discount", e.target.value)} />
              </SimpleGrid>
              <Textarea label="Notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} minRows={2} />
              <Button color="orange" onClick={handleSave} loading={busy === "save"} w={isMobile ? "100%" : "fit-content"}>
                Save Changes
              </Button>
            </Stack>
          )}
          </Tabs.Panel>

          <Tabs.Panel value="followups" pt="md">
            <LeadFollowups
              followups={followups}
              isLoading={isFollowupsLoading}
              followupForm={followupForm}
              onFollowupFormChange={handleFollowupFormChange}
              onSubmit={handleFollowupSubmit}
              isSubmitting={busy === "followup"}
            />
          </Tabs.Panel>

          <Tabs.Panel value="payment-link" pt="md">
            <Stack gap="md">
              <Checkbox
                checked={makePaymentLink}
                onChange={(e) => handleMakePaymentToggle(e.currentTarget.checked)}
                label="Generate online payment link for this quotation"
                styles={{ label: { fontWeight: 500 } }}
              />

              <Paper withBorder p="md" radius="md">
                <Text fw={600} mb="sm">Requirements</Text>
                <Stack gap={6}>
                  {requirements.map((r) => (
                    <Group key={r.label} gap="xs" wrap="nowrap">
                      {r.ok ? (
                        <IconCheck size={16} color="#40c057" style={{ flexShrink: 0 }} />
                      ) : (
                        <IconX size={16} color="#fa5252" style={{ flexShrink: 0 }} />
                      )}
                      <Text size="sm" c={r.ok ? undefined : "dimmed"}>{r.label}</Text>
                    </Group>
                  ))}
                </Stack>
              </Paper>

              {paymentUrl ? (
                <Paper withBorder p="md" radius="md">
                  <Text fw={600} mb="sm">Payment Link</Text>
                  <PaymentLinkBox url={paymentUrl} />
                  {linkData?.expires_on ? (
                    <Text size="xs" c="dimmed" mt="xs">Expires: {linkData.expires_on}</Text>
                  ) : null}
                  <Text size="xs" c="dimmed" mt="xs">
                    Sent via email & WhatsApp. Click Re-generate below to create a new link.
                  </Text>
                </Paper>
              ) : null}

              <Paper withBorder p="md" radius="md">
                <Text fw={600} mb="sm">Payment Gateway</Text>
                <Select
                  label="Gateway"
                  data={[{ value: "", label: "— Not set —" }, ...PAYMENT_GATEWAY_OPTIONS]}
                  value={gateway}
                  onChange={(v) => setGateway(v || "")}
                  mb="sm"
                />
                <Button variant="default" onClick={handleSaveGateway} loading={busy === "save"}>
                  Save Gateway
                </Button>
              </Paper>

              <Stack gap={4}>
                <Button
                  color="orange"
                  onClick={handleGenerateLink}
                  loading={busy === "link"}
                  disabled={!canGenerate}
                  w={isMobile ? "100%" : "fit-content"}
                >
                  {paymentUrl ? "Re-generate Link" : "Generate Link"}
                </Button>
                <Text size="xs" c="dimmed">
                  Sends link via email & WhatsApp to the customer.
                </Text>
              </Stack>
            </Stack>
          </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Drawer>
  );
};

export default QuotationDrawer;
